import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, BookOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";

function BookCover({ book }) {
  if (book.cover_image) {
    return (
      <img
        src={book.cover_image}
        alt={book.title}
        loading="lazy"
        className="block w-full h-full object-cover"
      />
    );
  }
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ backgroundColor: book.cover_color || "#0B1E36" }}
    >
      <div className="absolute inset-3 border border-[#C5A059]/40" />
      <div className="absolute inset-0 p-6 flex flex-col justify-between">
        <p className="text-[0.55rem] uppercase tracking-[0.3em] text-[#C5A059]">
          Carlos Medeiros
        </p>
        <h3 className="font-serif-display text-xl text-[#C5A059] leading-tight">
          {book.title}
        </h3>
        <span className="font-serif-display text-base text-[#C5A059] self-end">
          ✦
        </span>
      </div>
    </div>
  );
}

export default function LivrosIndex() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const r = useReveal();

  useEffect(() => {
    trackPageView();
    document.title = "Livros · Carlos Medeiros";
    api
      .get("/books")
      .then(({ data }) => setBooks(Array.isArray(data) ? data : []))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = books.find((b) => b.featured) || books[0];
  const others = books.filter((b) => b !== featured);

  return (
    <div data-testid="livros-index-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#0B1E36] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o início
          </Link>

          <header ref={r} className="reveal mt-8 mb-14 text-center">
            <div className="flex items-center justify-center mb-6">
              <span className="accent-rule" />
              <span className="eyebrow">Livros do Pastor Carlos Medeiros</span>
            </div>
            <h1
              data-testid="livros-title"
              className="font-serif-display text-[#0B1E36] text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.028em] font-medium"
            >
              Palavra que <span className="italic text-[#C5A059]">permanece</span>.
            </h1>
            <p className="mt-6 text-[1.05rem] md:text-[1.15rem] text-[#6B7280] leading-[1.85] font-light max-w-2xl mx-auto">
              Cada livro é fruto de anos de estudo, oração e experiência
              pastoral. Escritos para fortalecer sua fé, sua família e seu
              caminho com Cristo.
            </p>
          </header>

          {loading && (
            <div className="text-center py-20 text-[#6B7280]" data-testid="livros-loading">
              Carregando livros...
            </div>
          )}

          {!loading && books.length === 0 && (
            <div
              className="text-center py-20 text-[#6B7280]"
              data-testid="livros-empty"
            >
              Novos livros serão anunciados em breve.
            </div>
          )}

          {!loading && featured && (
            <section data-testid="livros-featured" className="mb-16">
              <Link
                to={`/livros/${featured.slug}`}
                className="group grid md:grid-cols-[280px_1fr] gap-8 bg-white rounded-xl border border-[#E5E7EB] p-6 md:p-10 hover:shadow-xl hover:border-[#C5A059] transition-all"
              >
                <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-lg">
                  <BookCover book={featured} />
                </div>
                <div className="flex flex-col justify-center">
                  <span className="eyebrow text-[#C5A059] mb-3">Destaque</span>
                  <h2 className="font-serif-display text-[#0B1E36] text-3xl md:text-4xl leading-[1.1] tracking-[-0.02em] font-medium">
                    {featured.title}
                  </h2>
                  {featured.subtitle && (
                    <p className="mt-3 text-[#6B7280] font-light italic">
                      {featured.subtitle}
                    </p>
                  )}
                  <p className="mt-5 text-[#0B1E36] leading-[1.75] font-light">
                    {featured.short_description}
                  </p>
                  <div className="mt-6 flex items-center gap-4">
                    {featured.price && (
                      <span
                        data-testid={`livro-price-${featured.slug}`}
                        className="text-[#0B1E36] font-medium text-lg"
                      >
                        {featured.price}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-sm text-[#C5A059] font-medium group-hover:gap-2 transition-all">
                      Conhecer o livro
                      <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </section>
          )}

          {!loading && others.length > 0 && (
            <section data-testid="livros-grid">
              <div className="flex items-center mb-8">
                <span className="accent-rule" />
                <span className="eyebrow">Todos os livros</span>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {others.map((b) => (
                  <Link
                    key={b.slug}
                    to={`/livros/${b.slug}`}
                    data-testid={`livro-card-${b.slug}`}
                    className="group bg-white rounded-xl border border-[#E5E7EB] overflow-hidden hover:shadow-lg hover:border-[#C5A059] transition-all"
                  >
                    <div className="aspect-[3/4]">
                      <BookCover book={b} />
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif-display text-[#0B1E36] text-xl leading-tight tracking-[-0.01em]">
                        {b.title}
                      </h3>
                      {b.short_description && (
                        <p className="mt-2 text-sm text-[#6B7280] leading-relaxed line-clamp-3">
                          {b.short_description}
                        </p>
                      )}
                      <div className="mt-4 flex items-center justify-between">
                        {b.price && (
                          <span className="text-sm text-[#0B1E36] font-medium">
                            {b.price}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs text-[#C5A059] font-medium group-hover:gap-2 transition-all">
                          Ver detalhes
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="mt-20 bg-[#0B1E36] text-white rounded-xl p-8 md:p-12 text-center">
            <BookOpen className="w-8 h-8 text-[#C5A059] mx-auto mb-4" />
            <h3 className="font-serif-display text-2xl md:text-3xl mb-3">
              Um livro pode transformar sua caminhada
            </h3>
            <p className="text-white/70 max-w-xl mx-auto mb-2 font-light leading-relaxed">
              Cada obra do pastor Carlos foi escrita com propósito pastoral: te
              conduzir a Cristo, fortalecer sua família e restaurar sua fé.
            </p>
          </section>
        </div>
      </main>

      <FloatingWhatsApp />
      <Footer />
    </div>
  );
}
